import React, { useCallback, useLayoutEffect, useRef, FC } from 'react';
import cls from 'classnames';

import { LazyImgix } from 'src/explore/elements/LazyImgix';
import { INavItem } from 'src/prize/types/shoppe';

import { Link } from '../../../../atoms/Link';

import styles from './index.module.sass';

const sizeVertical = ( element?: HTMLElement ) => {
  if ( !element ) return '0';

  const elementStyle = getComputedStyle( element );
  return `${
    element.offsetHeight +
    parseInt( elementStyle.marginTop, 10 ) +
    parseInt( elementStyle.marginBottom, 10 )
  }px`;
};

interface DropdownProps {
  className?: {
    childLink?: string;
    container?: string;
    item: string;
    link?: string;
  };
  desktopBreakpoint?: 'md' | 'lg';
  isExpanded: boolean;
  links?: INavItem[];
}

export const Dropdown: FC<DropdownProps> = ({
  className,
  desktopBreakpoint,
  isExpanded,
  links,
}) => {
  const containerRef = useRef<HTMLDivElement>( null );
  const contentRef = useRef<HTMLUListElement>( null );

  const size = useCallback(() => {
    const expandSize = sizeVertical( contentRef.current );
    const options = {
      duration: 200,
      easing: 'ease-out',
    };

    return {
      collapse: () =>
        containerRef.current?.animate([{ height: expandSize }, { height: 0 }], options ),
      expand: () => containerRef.current?.animate([{ height: 0 }, { height: expandSize }], options ),
    };
  }, []);

  useLayoutEffect(() => {
    if ( isExpanded ) {
      size().expand();
    } else {
      size().collapse();
    }
  }, [ isExpanded, size ]);

  return !links || links.length === 0 ? null : (
    <div
      className={cls(
        styles.container,
        styles[`overlay-${desktopBreakpoint}`],
        { [styles.open]: isExpanded },
        className?.container
      )}
      ref={containerRef}
    >
      <ul
        className={cls( styles.content, styles[`overlay-${desktopBreakpoint}`], {
          [styles.open]: isExpanded,
        })}
        ref={contentRef}
      >
        {links.map(( link ) => (
          <li className={cls( styles.item, className?.item )} key={link.id}>
            <Link
              className={cls(
                styles.link,
                styles[`overlay-${desktopBreakpoint}`],
                className?.link,
                className?.childLink
              )}
              href={link.url}
            >
              {link.featuredImageUrl && (
                <LazyImgix
                  alt={link.title}
                  className={styles.image}
                  height={48}
                  src={link.featuredImageUrl}
                  width={48}
                />
              )}

              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
